/**
 * COMPONENTE DE INDICADOR DE CONEXIÓN
 * 
 * Muestra el estado de la conexión en tiempo real:
 * - Verde: Conectado
 * - Amarillo: Reconectando
 * - Rojo: Desconectado/Error
 */

import { Wifi, WifiOff, Loader2, AlertTriangle } from 'lucide-react';
import { ConnectionStatus } from '@/hooks/useStableConnection';

const ConnectionIndicator = ({ 
  status, 
  lastUpdate, 
  retryCount, 
  onReconnect,
  className = "" 
}) => {
  const getTimeSinceUpdate = () => {
    if (!lastUpdate) return '';
    const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
    if (seconds < 60) return `hace ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `hace ${minutes}m`;
  };

  const getStatusConfig = () => {
    switch (status) {
      case ConnectionStatus.CONNECTED:
        return {
          icon: <Wifi className="w-3.5 h-3.5" />,
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/30',
          pulseColor: 'bg-emerald-500',
          text: 'Conectado',
          showPulse: true
        };
      case ConnectionStatus.RECONNECTING:
        return {
          icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          pulseColor: 'bg-amber-500',
          text: `Reconectando${retryCount > 0 ? ` (${retryCount})` : ''}...`,
          showPulse: false
        };
      case ConnectionStatus.DISCONNECTED:
        return {
          icon: <WifiOff className="w-3.5 h-3.5" />,
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          pulseColor: 'bg-red-500',
          text: 'Sin conexión',
          showPulse: false
        };
      case ConnectionStatus.ERROR:
        return {
          icon: <AlertTriangle className="w-3.5 h-3.5" />,
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          pulseColor: 'bg-red-500',
          text: 'Error de conexión',
          showPulse: false
        };
      default:
        return {
          icon: <Wifi className="w-3.5 h-3.5" />,
          color: 'text-zinc-400',
          bgColor: 'bg-zinc-500/10',
          borderColor: 'border-zinc-500/30',
          pulseColor: 'bg-zinc-500',
          text: 'Desconocido',
          showPulse: false
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div 
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded ${config.bgColor} border ${config.borderColor} ${className}`}
      data-testid="connection-indicator"
    >
      {/* Pulse indicator */}
      {config.showPulse && (
        <span className="relative flex h-2 w-2">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
          <span className={`relative inline-flex rounded-full h-2 w-2 ${config.pulseColor}`}></span>
        </span>
      )}
      
      {/* Icon */}
      <span className={config.color}>
        {config.icon}
      </span>
      
      {/* Status text */}
      <span className={`text-xs font-mono ${config.color} hidden sm:inline`}>
        {config.text}
      </span>
      
      {/* Last update time */}
      {status === ConnectionStatus.CONNECTED && lastUpdate && (
        <span className="text-[10px] text-zinc-500 hidden md:inline">
          {getTimeSinceUpdate()}
        </span>
      )}
      
      {/* Reconnect button for error state */}
      {(status === ConnectionStatus.ERROR || status === ConnectionStatus.DISCONNECTED) && onReconnect && (
        <button
          onClick={onReconnect}
          className="text-xs text-blue-400 hover:text-blue-300 underline ml-1"
        >
          Reintentar
        </button>
      )}
    </div>
  );
};

export default ConnectionIndicator;
